import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScope } from '../../store/scope';
import {
  useAssignments,
  useAssignmentMutations,
  useStalls,
  useRacks,
} from '../api';
import { PageLoader, ErrorState, EmptyState, StatusBadge, Pagination } from '../../components/ui';
import { ProductThumb } from '../../visitor/ProductThumb';
import { money, num, weight } from '../../lib/format';
import { useToast } from '../../components/Toast';
import { ApiRequestError } from '../../lib/api';
import { AssignProductsModal } from '../AssignProductsModal';
import { Modal } from '../../components/Modal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import type { Assignment } from '../../lib/types';

export function AssignmentsPage() {
  const { participationId } = useScope();
  const toast = useToast();

  const [stallId, setStallId] = useState<string>('');
  const [rackId, setRackId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const stalls = useStalls(participationId ?? undefined);
  const racks = useRacks(stallId || undefined);
  const list = useAssignments(participationId, {
    stallId: stallId || undefined,
    rackId: rackId || undefined,
    search: search || undefined,
    page,
    limit: 30,
  });
  const mut = useAssignmentMutations(participationId);

  const [assignOpen, setAssignOpen] = useState(false);
  const [editing, setEditing] = useState<Assignment | null>(null);
  const [moving, setMoving] = useState<Assignment | null>(null);
  const [removing, setRemoving] = useState<Assignment | null>(null);

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const stallList = stalls.data?.data ?? [];
  const rackList = racks.data?.data ?? [];
  const selectedStall = stallList.find((s) => s._id === stallId);
  const selectedRack = rackList.find((r) => r._id === rackId);

  if (!participationId) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-ink-900">Product Assignments</h1>
        <EmptyState
          title="Choose an expo first"
          hint="Use the “Expo focus” selector in the top bar to pick which exhibition you’re assigning products to."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">Product Assignments</h1>
          <p className="text-sm text-ink-400">
            {stalls.data ? `${stallList.length} stalls` : ' '} ·{' '}
            {list.data?.meta ? `${num(list.data.meta.total)} placements` : ' '}
          </p>
        </div>
        <button
          className="btn-primary"
          disabled={!rackId}
          title={rackId ? '' : 'Select a rack to assign into'}
          onClick={() => setAssignOpen(true)}
        >
          + Assign products{selectedRack ? ` to ${selectedRack.rackNumber}` : ''}
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        {/* Stall / rack picker */}
        <div className="card h-fit p-3">
          <p className="label">Stall</p>
          {stalls.isLoading ? (
            <PageLoader label="…" />
          ) : (
            <div className="space-y-1">
              <button
                className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${!stallId ? 'bg-ink-900 text-white' : 'hover:bg-ink-100'}`}
                onClick={() => {
                  setStallId('');
                  setRackId('');
                  setPage(1);
                }}
              >
                All stalls
              </button>
              {stallList.map((s) => (
                <button
                  key={s._id}
                  className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${stallId === s._id ? 'bg-ink-900 text-white' : 'hover:bg-ink-100'}`}
                  onClick={() => {
                    setStallId(s._id);
                    setRackId('');
                    setPage(1);
                  }}
                >
                  {s.stallNumber}
                  <span className={stallId === s._id ? 'text-ink-300' : 'text-ink-400'}> · {s.rackCount} racks</span>
                </button>
              ))}
              {stalls.data && stallList.length === 0 && (
                <p className="px-2 pt-2 text-xs text-ink-400">
                  No stalls in this expo yet.{' '}
                  <Link to={`/admin/participations/${participationId}`} className="text-gold-600 hover:underline">
                    Add stalls & racks
                  </Link>{' '}
                  first.
                </p>
              )}
            </div>
          )}

          {stallId && (
            <>
              <p className="label mt-3">Rack</p>
              <div className="space-y-1">
                <button
                  className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${!rackId ? 'bg-ink-800 text-white' : 'hover:bg-ink-100'}`}
                  onClick={() => {
                    setRackId('');
                    setPage(1);
                  }}
                >
                  All racks in {selectedStall?.stallNumber}
                </button>
                {rackList.map((r) => (
                  <button
                    key={r._id}
                    className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${rackId === r._id ? 'bg-ink-800 text-white' : 'hover:bg-ink-100'}`}
                    onClick={() => {
                      setRackId(r._id);
                      setPage(1);
                    }}
                  >
                    {r.rackNumber}
                    <span className={rackId === r._id ? 'text-ink-300' : 'text-ink-400'}> · {r.assignedProductCount}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Assignments table */}
        <div className="space-y-3">
          <div className="card flex flex-wrap items-center gap-2 p-3">
            <input
              className="input max-w-xs"
              placeholder="Search barcode / Photo ID / name…"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
            <span className="ml-auto text-sm text-ink-400">
              {selectedRack
                ? `Stall ${selectedStall?.stallNumber} · Rack ${selectedRack.rackNumber}`
                : selectedStall
                  ? `Stall ${selectedStall.stallNumber} · all racks`
                  : 'All stalls'}
            </span>
          </div>

          {list.isLoading ? (
            <PageLoader />
          ) : list.error ? (
            <ErrorState message={(list.error as Error).message} onRetry={() => list.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No products placed here yet"
              hint={rackId ? 'Use “Assign products” to place jewellery on this rack.' : 'Pick a rack, then assign products.'}
              action={
                rackId ? (
                  <button className="btn-primary" onClick={() => setAssignOpen(true)}>
                    Assign products
                  </button>
                ) : undefined
              }
            />
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="table-head">
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2">Location</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      <th className="px-3 py-2 text-right">Available</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-50">
                    {rows.map((a) => (
                      <tr key={a._id} className="hover:bg-ink-50/60">
                        <td className="px-3 py-2 text-ink-400">{a.displayOrder}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="h-9 w-9 overflow-hidden rounded-md bg-ink-50">
                              <ProductThumb src={a.product?.images?.[0]?.thumbnailUrl} alt={a.product?.barcodeNo ?? ''} />
                            </div>
                            <div className="min-w-0">
                              <Link to={`/admin/jewellery/${a.productId}`} className="block font-medium hover:underline">
                                {a.product?.displayName || a.product?.photoId || a.product?.barcodeNo}
                              </Link>
                              <span className="font-mono text-xs text-ink-400">
                                #{a.product?.barcodeNo}
                                {a.product?.size ? ` · ${a.product.size}` : ''}
                                {a.product?.grossWeight ? ` · ${weight(a.product.grossWeight)}` : ''}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-ink-500">
                          {a.stallNumber} / {a.rackNumber}
                        </td>
                        <td className="px-3 py-2 text-right">{money(a.displayPrice)}</td>
                        <td className="px-3 py-2 text-right">
                          {a.initialQuantity > 0 ? num(a.availableQuantity) : <span className="text-ink-300">∞</span>}
                        </td>
                        <td className="px-3 py-2">
                          <StatusBadge status={a.status} />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex justify-end gap-1">
                            <button className="btn-ghost px-2 py-1" onClick={() => setEditing(a)}>
                              Edit
                            </button>
                            <button className="btn-ghost px-2 py-1" onClick={() => setMoving(a)}>
                              Move
                            </button>
                            <button
                              className="btn-ghost px-2 py-1 text-red-600 hover:bg-red-50"
                              onClick={() => setRemoving(a)}
                            >
                              Unassign
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3">
                <Pagination page={page} pages={list.data?.meta?.pages ?? 1} onPage={setPage} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assign modal */}
      <AssignProductsModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        participationId={participationId}
        stallId={stallId}
        rackId={rackId}
        rackLabel={selectedRack ? `${selectedStall?.stallNumber} / ${selectedRack.rackNumber}` : ''}
        onDone={() => {
          list.refetch();
          stalls.refetch();
          racks.refetch();
        }}
      />

      {/* Edit assignment */}
      <AssignmentEditModal
        assignment={editing}
        busy={mut.update.isPending}
        onClose={() => setEditing(null)}
        onSave={async (body) => {
          try {
            await mut.update.mutateAsync({ id: editing!._id, body });
            toast('Assignment updated', 'success');
            setEditing(null);
          } catch (e) {
            toast(e instanceof ApiRequestError ? e.message : 'Update failed', 'error');
          }
        }}
      />

      {/* Move assignment */}
      <MoveAssignmentModal
        assignment={moving}
        participationId={participationId}
        busy={mut.move.isPending}
        onClose={() => setMoving(null)}
        onMove={async (body) => {
          try {
            await mut.move.mutateAsync({ id: moving!._id, body });
            toast('Product moved', 'success');
            setMoving(null);
            list.refetch();
          } catch (e) {
            toast(e instanceof ApiRequestError ? e.message : 'Move failed', 'error');
          }
        }}
      />

      <ConfirmDialog
        open={!!removing}
        title="Unassign product?"
        tone="danger"
        confirmLabel="Unassign"
        busy={mut.remove.isPending}
        message={
          removing && (
            <p>
              Remove <b>{removing.product?.barcodeNo}</b> from {removing.stallNumber}/{removing.rackNumber}?
              If it’s in a booking the row is kept as <i>inactive</i> for history; otherwise it’s deleted.
            </p>
          )
        }
        onCancel={() => setRemoving(null)}
        onConfirm={async () => {
          try {
            await mut.remove.mutateAsync(removing!._id);
            toast('Unassigned', 'success');
            setRemoving(null);
            list.refetch();
          } catch (e) {
            toast(e instanceof ApiRequestError ? e.message : 'Failed', 'error');
          }
        }}
      />
    </div>
  );
}

// ── Edit modal ──
function AssignmentEditModal({
  assignment,
  busy,
  onClose,
  onSave,
}: {
  assignment: Assignment | null;
  busy: boolean;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [displayOrder, setDisplayOrder] = useState('0');
  const [displayPrice, setDisplayPrice] = useState('');
  const [availableQuantity, setAvailableQuantity] = useState('0');
  const [status, setStatus] = useState('active');

  useEffect(() => {
    if (assignment) {
      setDisplayOrder(String(assignment.displayOrder));
      setDisplayPrice(assignment.displayPrice ?? '');
      setAvailableQuantity(String(assignment.availableQuantity));
      setStatus(assignment.status);
    }
  }, [assignment]);

  if (!assignment) return null;
  return (
    <Modal
      open={!!assignment}
      onClose={onClose}
      title={`Edit ${assignment.product?.barcodeNo} on ${assignment.rackNumber}`}
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy}
            onClick={() =>
              onSave({
                displayOrder: Number(displayOrder) || 0,
                displayPrice: displayPrice === '' ? null : displayPrice,
                availableQuantity: Number(availableQuantity) || 0,
                status,
              })
            }
          >
            Save
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="label">Display order</span>
          <input className="input" inputMode="numeric" value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} />
        </label>
        <label className="block">
          <span className="label">Status</span>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
            <option value="sold_out">sold_out</option>
          </select>
        </label>
        <label className="block">
          <span className="label">Display price (₹) — blank = use product price</span>
          <input className="input" inputMode="decimal" value={displayPrice} onChange={(e) => setDisplayPrice(e.target.value)} />
        </label>
        <label className="block">
          <span className="label">Available quantity (0 = untracked)</span>
          <input
            className="input"
            inputMode="numeric"
            value={availableQuantity}
            onChange={(e) => setAvailableQuantity(e.target.value)}
          />
        </label>
      </div>
    </Modal>
  );
}

// ── Move modal ──
function MoveAssignmentModal({
  assignment,
  participationId,
  busy,
  onClose,
  onMove,
}: {
  assignment: Assignment | null;
  participationId: string;
  busy: boolean;
  onClose: () => void;
  onMove: (body: Record<string, unknown>) => void;
}) {
  const stalls = useStalls(assignment ? participationId : undefined);
  const [toStallId, setToStallId] = useState('');
  const racks = useRacks(toStallId || undefined);
  const [toRackId, setToRackId] = useState('');
  const [keepPrice, setKeepPrice] = useState(true);
  const [keepQty, setKeepQty] = useState(true);

  useEffect(() => {
    setToStallId('');
    setToRackId('');
  }, [assignment]);

  if (!assignment) return null;
  const stallList = stalls.data?.data ?? [];
  const rackList = (racks.data?.data ?? []).filter((r) => r._id !== assignment.rackId);

  return (
    <Modal
      open={!!assignment}
      onClose={onClose}
      title={`Move ${assignment.product?.barcodeNo}`}
      footer={
        <>
          <button className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || !toStallId || !toRackId}
            onClick={() =>
              onMove({
                toStallId,
                toRackId,
                ...(keepPrice ? {} : { displayPrice: null }),
                ...(keepQty ? {} : { availableQuantity: 0 }),
              })
            }
          >
            Move here
          </button>
        </>
      }
    >
      <p className="mb-3 text-sm text-ink-500">
        Currently on <b>{assignment.stallNumber} / {assignment.rackNumber}</b>. Existing bookings keep
        their original location.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="label">To stall</span>
          <select className="input" value={toStallId} onChange={(e) => { setToStallId(e.target.value); setToRackId(''); }}>
            <option value="">Select…</option>
            {stallList.map((s) => (
              <option key={s._id} value={s._id}>
                {s.stallNumber}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="label">To rack</span>
          <select className="input" value={toRackId} onChange={(e) => setToRackId(e.target.value)} disabled={!toStallId}>
            <option value="">Select…</option>
            {rackList.map((r) => (
              <option key={r._id} value={r._id}>
                {r.rackNumber}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-3 space-y-1 text-xs text-ink-600">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={keepPrice} onChange={(e) => setKeepPrice(e.target.checked)} /> Keep current price
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={keepQty} onChange={(e) => setKeepQty(e.target.checked)} /> Keep current available quantity
        </label>
      </div>
    </Modal>
  );
}
